import React, { useMemo, useState } from 'react';
import PerfHubSectionInfo from './PerfHubSectionInfo';
import { PERF_HUB_OVERVIEW_HELP } from './perfHubOverviewHelp';
import { COMMENT_FILTERS, sortCommentCreators } from './perfHubCommentFilter';
import { downloadCommentIntelligenceCsv } from './perfHubCommentIntelligenceExport';
import PerfHubBuyerQuestionsModal from './PerfHubBuyerQuestionsModal';

const TOP_QUESTIONS_PREVIEW = 4;

const CONFIDENCE_LABELS = {
  high: 'High confidence',
  medium: 'Medium confidence',
  low: 'Low confidence',
};

function copyText(text) {
  if (!text) return;
  navigator.clipboard?.writeText(text).catch(() => {});
}

function formatLastBuilt(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function BuyerQuestionItem({ q }) {
  return (
    <li
      className={`ph-comments-question-item ${
        q.unanswered ? 'ph-comments-question-item--unanswered' : ''
      }`}
    >
      <strong>{q.question}</strong>
      <p className="ph-cell-muted ph-comments-question-meta">
        ~{q.frequency_estimate} mention{q.frequency_estimate === 1 ? '' : 's'}
        {q.total_likes > 0 ? ` · ${q.total_likes} comment likes` : ''}
        {q.unanswered ? ' · No creator reply yet' : ' · Replied'}
      </p>
    </li>
  );
}

function SentimentBar({ sentiment }) {
  const pos = sentiment?.positive_pct ?? 0;
  const neu = sentiment?.neutral_pct ?? 0;
  const neg = sentiment?.negative_pct ?? 0;
  return (
    <>
      <div className="ph-comments-sentiment-bar" aria-hidden>
        {pos > 0 && (
          <span
            className="ph-comments-sentiment-seg--pos"
            style={{ width: `${pos}%` }}
          />
        )}
        {neu > 0 && (
          <span
            className="ph-comments-sentiment-seg--neu"
            style={{ width: `${neu}%` }}
          />
        )}
        {neg > 0 && (
          <span
            className="ph-comments-sentiment-seg--neg"
            style={{ width: `${neg}%` }}
          />
        )}
      </div>
      <div className="ph-comments-sentiment-legend">
        <span>Positive {pos}%</span>
        <span>Neutral {neu}%</span>
        <span>Negative {neg}%</span>
      </div>
    </>
  );
}

export default function PerfHubCommentIntelligencePanel({
  commentIntelligence,
  busy,
  commentsRefreshBusy,
  canRefreshComments,
  onRefreshComments,
  onAddCrmNote,
  campaignName,
  exportEnabled = true,
}) {
  const [filterId, setFilterId] = useState('all');
  const [questionsModalOpen, setQuestionsModalOpen] = useState(false);
  const summary = commentIntelligence?.summary || {};
  const sentiment = commentIntelligence?.sentiment || {};
  // ``posts_analyzed`` is the new field; ``connected_posts`` kept as legacy fallback.
  const connected = summary.posts_analyzed ?? summary.connected_posts ?? 0;
  const lastBuilt = formatLastBuilt(commentIntelligence?.ingest?.last_built_at);
  const refreshDisabled = busy || commentsRefreshBusy || !canRefreshComments;
  const isRefreshing = commentsRefreshBusy;

  const topQuestions = commentIntelligence?.top_questions || [];
  const previewQuestions = topQuestions.slice(0, TOP_QUESTIONS_PREVIEW);
  const hasMoreQuestions = topQuestions.length > TOP_QUESTIONS_PREVIEW;

  const allCreators = commentIntelligence?.creators || [];
  const activeFilterId = COMMENT_FILTERS.some((f) => f.id === filterId)
    ? filterId
    : 'all';
  const sortedCreators = useMemo(
    () => sortCommentCreators(allCreators, activeFilterId),
    [allCreators, activeFilterId]
  );

  const canDownload = exportEnabled && sortedCreators.length > 0;

  const handleDownload = () => {
    if (!canDownload) return;
    downloadCommentIntelligenceCsv(sortedCreators, {
      campaignName,
      filterId: activeFilterId,
      commentIntelligence,
    });
  };

  if (!commentIntelligence) return null;

  return (
    <section
      className={`ph-comments-panel${isRefreshing ? ' ph-comments-panel--refreshing' : ''}`}
      aria-labelledby="ph-comments-heading"
    >
      <div className="ph-comments-head">
        <div className="ph-comments-head-copy">
          <h4 id="ph-comments-heading" className="ph-comments-title">
            <span aria-hidden>💬</span>
            Comment Intelligence
            <PerfHubSectionInfo label="Comment Intelligence">
              {PERF_HUB_OVERVIEW_HELP.commentIntelligence}
            </PerfHubSectionInfo>
          </h4>
          <p className="ph-comments-tagline">
            {summary.tagline || 'Turn comments into sales and fixes.'}
          </p>
        </div>
        <div className="ph-comments-head-side">
          <div className="ph-comments-head-meta">
            {commentIntelligence.llm_used && (
              <span className="ph-comments-llm-badge">AI-analyzed</span>
            )}
            {lastBuilt && (
              <span className="ph-comments-llm-badge">Updated {lastBuilt}</span>
            )}
          </div>
          <div className="ph-comments-head-toolbar">
            <button
              type="button"
              className="btn-secondary ph-comments-tool-btn"
              disabled={!canDownload || busy || commentsRefreshBusy}
              onClick={handleDownload}
              title={
                canDownload
                  ? 'Download creator-level comment insights (current sort)'
                  : exportEnabled
                    ? 'No creator rows to export'
                    : 'CSV export is available on paid plans'
              }
            >
              Download CSV
            </button>
            <button
              type="button"
              className="btn-primary ph-comments-refresh-btn"
              disabled={refreshDisabled}
              onClick={onRefreshComments}
              aria-busy={isRefreshing}
              title={
                canRefreshComments
                  ? 'Fetch latest comments and regenerate insights (does not refresh post metrics)'
                  : 'Available on live campaigns with synced posts'
              }
            >
              <span className="ph-comments-refresh-btn-label">Refresh comments</span>
              <span className="ph-comments-refresh-btn-busy">Refreshing…</span>
            </button>
          </div>
        </div>
      </div>

      {connected === 0 ? (
        <div className="ph-dna-empty">
          <p>{summary.headline || 'Refresh comments to pull and analyze post comments.'}</p>
          {canRefreshComments && (
            <button
              type="button"
              className={`btn-primary ph-comments-refresh-btn ph-comments-refresh-btn--solo${
                isRefreshing ? ' ph-comments-refresh-btn--busy' : ''
              }`}
              disabled={refreshDisabled}
              onClick={onRefreshComments}
              aria-busy={isRefreshing}
            >
              <span className="ph-comments-refresh-btn-label">Refresh comments</span>
              <span className="ph-comments-refresh-btn-busy">Refreshing…</span>
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="ph-dna-meta">
            <span className={`ph-dna-confidence ph-dna-confidence--${summary.confidence || 'low'}`}>
              {CONFIDENCE_LABELS[summary.confidence] || 'Low confidence'}
            </span>
            <span className="ph-dna-meta-count">
              {summary.total_comments_ingested ?? 0} comments · {connected} post
              {connected === 1 ? '' : 's'}
            </span>
          </div>

          {summary.headline && <p className="ph-dna-headline">{summary.headline}</p>}

          {sentiment.spike_alert && (
            <div className="ph-comments-spike" role="alert">
              ⚠️ Sentiment spike: {sentiment.spike_reason || 'Negative comments increased.'}
            </div>
          )}

          <SentimentBar sentiment={sentiment} />

          {(commentIntelligence.themes || []).length > 0 && (
            <>
              <h5 className="ph-comments-subtitle">Comment themes</h5>
              <div className="ph-comments-theme-grid">
                {commentIntelligence.themes.map((t) => (
                  <div key={t.id} className="ph-comments-theme-card">
                    <span className="ph-comments-theme-label">{t.label}</span>
                    <span className="ph-comments-theme-count">({t.count ?? t.count_estimate})</span>
                    {t.summary && <p className="ph-comments-theme-summary">{t.summary}</p>}
                    {(t.examples || []).length > 0 && (
                      <p className="ph-comments-theme-summary ph-cell-muted">
                        “{t.examples[0]}”
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {topQuestions.length > 0 && (
            <>
              <h5 className="ph-comments-subtitle">Top buyer questions</h5>
              <ul className="ph-comments-question-list">
                {previewQuestions.map((q) => (
                  <BuyerQuestionItem key={q.question?.slice(0, 48)} q={q} />
                ))}
              </ul>
              {hasMoreQuestions && (
                <button
                  type="button"
                  className="btn-outline ph-comments-explore-questions-btn"
                  onClick={() => setQuestionsModalOpen(true)}
                >
                  Explore all buyer questions ({topQuestions.length})
                </button>
              )}
              {questionsModalOpen && (
                <PerfHubBuyerQuestionsModal
                  questions={topQuestions}
                  campaignName={campaignName}
                  onClose={() => setQuestionsModalOpen(false)}
                />
              )}
            </>
          )}

          {allCreators.length > 0 && (
            <>
              <div className="ph-comments-creator-head">
                <h5 className="ph-comments-subtitle ph-comments-subtitle--inline">
                  Creator comment engagement
                </h5>
                <div className="ph-comments-creator-head-tools">
                  <label className="ph-comments-filter-label" htmlFor="ph-comments-filter-select">
                    Sort by
                  </label>
                  <select
                    id="ph-comments-filter-select"
                    className="ph-comments-filter-select"
                    value={activeFilterId}
                    onChange={(e) => setFilterId(e.target.value)}
                    aria-label="Sort creators by comment insights"
                  >
                    {COMMENT_FILTERS.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                  <span className="ph-comments-filter-count ph-cell-muted">
                    {allCreators.length} creator{allCreators.length === 1 ? '' : 's'}
                  </span>
                </div>
              </div>

              {sortedCreators.length > 0 ? (
                <div className="ph-comments-creator-table-wrap">
                  <table className="funds-table enhanced">
                    <thead>
                      <tr>
                        <th>Creator</th>
                        <th>Post</th>
                        <th>Comments</th>
                        <th title="Positive comments">+</th>
                        <th title="Neutral comments">○</th>
                        <th title="Negative comments">−</th>
                        <th>Questions</th>
                        <th>Unanswered</th>
                        <th>Response</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedCreators.map((c) => (
                        <tr key={c.row_id}>
                          <td>@{c.instagram_handle}</td>
                          <td className="ph-td-post-link">
                            {c.post_url ? (
                              <a
                                href={c.post_url}
                                className="ph-inline-link"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View post
                              </a>
                            ) : (
                              <span className="ph-cell-muted">—</span>
                            )}
                          </td>
                          <td>{c.ingested_count ?? '—'}</td>
                          <td>{c.positive_count ?? '—'}</td>
                          <td>{c.neutral_count ?? '—'}</td>
                          <td>{c.negative_count ?? '—'}</td>
                          <td>{c.question_count ?? '—'}</td>
                          <td>{c.unanswered_count ?? '—'}</td>
                          <td>
                            {c.response_rate_pct != null ? `${c.response_rate_pct}%` : '—'}
                          </td>
                          <td>
                            <span
                              className={`ph-comments-status--${c.status || 'no_questions'}`}
                            >
                              {c.status_label || '—'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="ph-cell-muted ph-comments-empty-filter">No creator data yet.</p>
              )}
            </>
          )}

          {(commentIntelligence.action_items || []).length > 0 && (
            <>
              <h5 className="ph-comments-subtitle">Suggested actions (raise with creator)</h5>
              {commentIntelligence.action_items.map((item) => (
                <div
                  key={item.id}
                  className={`ph-comments-action-card ph-comments-action-card--${item.priority || 'medium'}`}
                >
                  <strong>{item.title}</strong>
                  {item.detail && (
                    <p className="ph-cell-muted" style={{ margin: '6px 0 0', fontSize: '0.85rem' }}>
                      {item.detail}
                    </p>
                  )}
                  <div className="ph-comments-action-actions">
                    {item.suggested_crm_note && (
                      <>
                        <button
                          type="button"
                          className="btn-outline"
                          disabled={busy || commentsRefreshBusy}
                          onClick={() => copyText(item.suggested_crm_note)}
                        >
                          Copy note
                        </button>
                        {(item.creator_handles || []).map((h) => (
                          <button
                            key={h}
                            type="button"
                            className="btn-outline ph-btn-crm-quick"
                            disabled={busy || commentsRefreshBusy || !onAddCrmNote}
                            onClick={() => onAddCrmNote(h, item.suggested_crm_note)}
                          >
                            Save note for @{h}
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </>
      )}
    </section>
  );
}
